import { useMemo } from 'react'
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client'
import GlobalSearchFieldAnchor from '@broker-crm-common/GlobalSearch/GlobalSearchFieldAnchor/GlobalSearchFieldAnchor'
import { PermissionInput } from '@broker-crm-common/Permission/permission.types'
import usePermissionResolution from '@broker-crm-common/Permission/usePermissionResolution'
import { useNotifications, userType } from '@broker-crm-utils'
import { Box, useTheme } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router-dom'

import { Feature, Flag, FlagType, getUserOrganisationIds, useFlag, useOpen, useUserContext } from '@acre/utils'
import {
  PermissionGateName,
  PermissionScopeType,
  useGetCurrentUserQuery,
  useGetNotificationsQuery,
} from '@acre/graphql'
import { AcreSquareLogo, ExternalLink, FullPageLoadingSpinner, Nav, OptionsNavLink } from '@acre/design-system'

import useSourcingUrl from '../../utils/hooks/useSourcingUrl'
import { NotificationButton } from '../Notifications/Notifications'
import DashboardsNavPopover from './DashboardsNavPopover/DashboardsNavPopover'
import { isPageWithoutNav } from './Nav.helpers'
import NavContent from './NavContent'
import NavGlobalSearchModal from './NavGlobalSearchModal'
import PreviewModeButton from './PreviewModeButton'
import UserSettings from './UserSettings/UserSettings'

const MainNav = () => {
  const location = useLocation()

  const showBrokerAccounting = useFlag([FlagType.Feature, Feature.ReportCommissions], false)

  const { isVisible: isModalSearchVisible, open: openModalSearch, close: closeModalSearch } = useOpen(false)

  const user = useUserContext()
  const theme = useTheme()
  const hideClientList = useFlag([FlagType.Feature, Feature.HideClientList], false)

  const client = useApolloClient()

  // Start polling for notifications from socket
  const { clearNotificationsById, clearAllNotifcations } = useNotifications(
    client as ApolloClient<NormalizedCacheObject>,
  )

  const { data: notifications } = useGetNotificationsQuery({
    variables: { userId: String(user?.id) },
    skip: !user?.id,
  })

  const { data, loading } = useGetCurrentUserQuery()

  const sourcingUrl = useSourcingUrl()

  const {
    ViewBrokerDashboard,
    ViewCaseSection,
    ViewCaseCompliance,
    ViewClientSection,
    ViewMiDashboard,
    Accounting,
    EditClientRestricted,
  } = PermissionGateName

  const { userOrganisationIds, userId } = useMemo(() => {
    if (!user) {
      return {
        userOrganisationIds: [],
        userId: [],
      }
    }

    return {
      userOrganisationIds: [getUserOrganisationIds({ user }).userOrganisationId],
      userId: [user.id],
    }
  }, [user])

  const permissionInputs: PermissionInput[] = [
    {
      gates: [
        ViewBrokerDashboard,
        ViewClientSection,
        Accounting,
        EditClientRestricted,
        ViewCaseCompliance,
        ViewMiDashboard,
      ],
      entityIdsCollection: {
        [PermissionScopeType.organisation]: userOrganisationIds,
      },
    },
    {
      gates: [ViewCaseSection],
      entityIdsCollection: {
        [PermissionScopeType.organisation]: userOrganisationIds,
        [PermissionScopeType.user]: userId,
      },
    },
    {
      gates: [Accounting],
      entityIdsCollection: {
        [PermissionScopeType.user]: userId,
      },
    },
  ]

  const { gates, gatesByScope } = usePermissionResolution({
    inputs: permissionInputs,
  })

  const accountingNavLink = useMemo(() => {
    if (gatesByScope[PermissionScopeType.organisation]?.has(PermissionGateName.Accounting)) {
      return '/accounting/payments-due'
    }

    if (showBrokerAccounting && gatesByScope[PermissionScopeType.user]?.has(PermissionGateName.Accounting)) {
      return '/accounting/commission-statement'
    }

    return null
  }, [gatesByScope, showBrokerAccounting])

  const reminderRoles = [
    userType.principal,
    userType.administrator,
    userType.advisor,
    userType.firmManager,
    userType.modifySupport,
    userType.readOnlySupport,
  ]

  const displayComplianceLink =
    gates.includes(PermissionGateName.ViewCaseCompliance) && !gates.includes(PermissionGateName.ViewCaseSection)

  if (isPageWithoutNav(location?.pathname)) {
    return null
  }

  return (
    <>
      {loading && <FullPageLoadingSpinner />}

      {isModalSearchVisible && (
        <NavGlobalSearchModal isModalSearchVisible={isModalSearchVisible} closeModalSearch={closeModalSearch} />
      )}

      {Boolean(data) && (
        <Nav
          offset={theme.display.navHeightSmall.toString()}
          left={[
            <Box key="logo" display="flex" alignItems="center" ml={4} mr={12 / 8}>
              <AcreSquareLogo />
            </Box>,
            <DashboardsNavPopover gates={gates} key="DashboardsNavPopover" />,
            gates.includes(PermissionGateName.ViewCaseSection) && (
              <OptionsNavLink
                to="/cases/board-view"
                id="MainNavCases"
                key="MainNavCases"
                isActive={(location) => location.startsWith('/cases')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.cases" />
              </OptionsNavLink>
            ),
            displayComplianceLink && (
              <OptionsNavLink
                to="/cases/compliance-view"
                id="ComplianceCasesLink"
                key="ComplianceCasesLink"
                isActive={(location) => location.endsWith('/cases/compliance-view')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.casesCompliance" />
              </OptionsNavLink>
            ),
            gates.includes(PermissionGateName.ViewClientSection) &&
              !(hideClientList && user?.primary_role_id == userType.advisor) && (
                <OptionsNavLink
                  to="/clients"
                  id="MainNavClientsLink"
                  key="MainNavClientsLink"
                  isActive={(location) => location.startsWith('/clients')}
                  alwaysRedirect
                >
                  <FormattedMessage id="nav.clients" />
                </OptionsNavLink>
              ),
            user?.primary_role_id && reminderRoles.includes(user.primary_role_id) && (
              <OptionsNavLink
                to="/reminders"
                id="RemindersLink"
                key="RemindersLink"
                isActive={(location) => location.startsWith('/reminders')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.reminders" />
              </OptionsNavLink>
            ),

            <Flag keyPath={[FlagType.Feature, Feature.MI]} key="ReportsFlag" defaultValue={true}>
              <OptionsNavLink
                to={`/reports/${user?.organisation_id}`}
                id="ReportsLink"
                isActive={(location) => location.startsWith('/reports')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.reports" />
              </OptionsNavLink>
            </Flag>,

            // Allow for unique keys in each array element
            ...(user?.primary_role_id && reminderRoles.includes(user.primary_role_id)
              ? [
                  <ExternalLink href={sourcingUrl} key={sourcingUrl}>
                    <FormattedMessage id="nav.sourcing.title" />
                  </ExternalLink>,
                ]
              : []),
            accountingNavLink && (
              <OptionsNavLink
                to={accountingNavLink}
                id="MainNavAccountingLink"
                key="MainNavAccountingLink"
                isActive={(location) => location.startsWith('/accounting')}
                alwaysRedirect
              >
                <FormattedMessage id="nav.accounting" />
              </OptionsNavLink>
            ),
          ]}
          right={
            <Box display="flex" alignItems="center" mr={3} ml={2} gap={2}>
              <GlobalSearchFieldAnchor
                onClick={() => {
                  openModalSearch()
                }}
                placeholder="Search"
              />
              <PreviewModeButton />
              <NotificationButton
                notifications={notifications?.notifications || []}
                onClearAll={clearAllNotifcations}
                onClearById={clearNotificationsById}
                key="NavGlobalNotifications"
              />
              <UserSettings
                firstName={user?.first_name}
                lastName={user?.last_name}
                organisationId={user?.organisation_id}
                userId={user?.id}
              />
            </Box>
          }
        >
          <NavContent pathname={location?.pathname} />
        </Nav>
      )}
    </>
  )
}

export default MainNav
